import {createSelector} from '@reduxjs/toolkit';
import {RootState} from '../store';
import type {SubscriptionPlan} from '../../domain/types';

export const subscriptionStateSelector = (state: RootState) => state.subscription;

export const productsSelector = (state: RootState) => subscriptionStateSelector(state).data.products;
export const subscriptionsSelector = (state: RootState) => subscriptionStateSelector(state).data.subscriptions;
export const paddleCustomerIdSelector = (state: RootState) => subscriptionStateSelector(state).data.customerId;

export const productsApiSelector = (state: RootState) => subscriptionStateSelector(state).api.products;
export const subscriptionsApiSelector = (state: RootState) => subscriptionStateSelector(state).api.subscriptions;
export const customerApiSelector = (state: RootState) => subscriptionStateSelector(state).api.customer;

export const monthlyProductSelector = createSelector(
  [productsSelector],
  products => products?.monthly ?? null
);

export const yearlyProductSelector = createSelector(
  [productsSelector],
  products => products?.yearly ?? null
);

/**
 * A subscription only counts as live while it is active or trialing. Everything else — paused,
 * cancelled, past due — leaves the account without Pro.
 *
 * `trialing` sitting alongside `active` is what makes a trial signup indistinguishable from a paid
 * one everywhere downstream: a trial user is a Pro user, with no separate state to render.
 */
export const activeSubscriptionSelector = createSelector(
  [subscriptionsSelector],
  subscriptions => subscriptions.find(sub => sub.status === 'active' || sub.status === 'trialing') ?? null
);

export const currentPlanSelector = createSelector(
  [activeSubscriptionSelector],
  (activeSubscription): SubscriptionPlan | null => {
    if (!activeSubscription) {
      return null;
    }
    return activeSubscription.billingCycle?.interval === 'year' ? 'pro_yearly' : 'pro_monthly';
  }
);

export const hasSubscriptionSelector = createSelector(
  [currentPlanSelector],
  plan => plan !== null
);

/**
 * Paused only matters when there is nothing live to fall back on — that is the case the amber
 * "Pro limits do not apply until you resume" notice is for.
 */
export const hasPausedSubscriptionSelector = createSelector(
  [subscriptionsSelector, activeSubscriptionSelector],
  (subscriptions, activeSubscription) => (
    !activeSubscription && subscriptions.some(sub => sub.status === 'paused')
  )
);
